<template>
  <!-- template for the modal component -->
  <transition name="modal" @after-enter="afterEnter" @after-leave="afterLeave">
    <div class="modal-mask" @keydown.esc="$emit('close')">
      <div class="modal-wrapper" @click="closeOnBackdrop && $emit('close')">
        <div class="modal-container"
             @click.stop
             role="dialog"
             aria-modal="true"
             :aria-labelledby="ariaLabelledby"
             ref="modalContainer"
             tabindex="-1">

          <div class="modal-header">

          </div>

          <div class="modal-body">
            <slot name="body">

            </slot>
          </div>

          <div class="modal-footer">
            <slot name="footer">
              <!-- With backdrop-close disabled, Esc would otherwise be the only
                   way out, so an explicit control is required. -->
              <button v-if="!closeOnBackdrop" class="modal-default-button" @click="$emit('close')">
                {{ $t('close') }}
              </button>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'Modal',
  data () {
    return {
      previousActiveElement: null,
      focusableElements: []
    }
  },
  props: {
    name: String,
    parent: String,
    // Was 'modal-title', but no element with that id exists anywhere, so the
    // dialog pointed screen readers at nothing. null omits the attribute.
    ariaLabelledby: {
      type: String,
      default: null
    },
    // Settings keeps click-outside-to-close; the recommendations modal disables
    // it so an accidental tap cannot destroy the only revenue surface.
    closeOnBackdrop: {
      type: Boolean,
      default: true
    }
  },

  mounted() {
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  },

  beforeDestroy() {
    // Restore body scroll
    document.body.style.overflow = '';
  },

  methods: {
    afterEnter() {
      // Store the previously focused element
      this.previousActiveElement = document.activeElement;

      // Focus the modal container
      this.$nextTick(() => {
        if (this.$refs.modalContainer) {
          this.$refs.modalContainer.focus();
        }

        // Get all focusable elements within the modal
        this.setupFocusTrap();
      });
    },

    afterLeave() {
      // Restore focus to the previously focused element
      if (this.previousActiveElement && this.previousActiveElement.focus) {
        this.previousActiveElement.focus();
      }
    },

    setupFocusTrap() {
      const modal = this.$refs.modalContainer;
      if (!modal) return;

      // Get all focusable elements
      const focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      this.focusableElements = Array.from(modal.querySelectorAll(focusableSelectors));

      // Add keydown listener for tab trap
      modal.addEventListener('keydown', this.handleTabKey);
    },

    handleTabKey(event) {
      if (event.key !== 'Tab') return;

      const firstFocusable = this.focusableElements[0];
      const lastFocusable = this.focusableElements[this.focusableElements.length - 1];

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      }
    }
  }
}
</script>

<style scoped>

.buttons {
  -webkit-border-radius: 6;
  -moz-border-radius: 6;
  border-radius: 6px;
  font-family: Arial;
  color: #00073F;
  font-size: 20px;
  padding: 10px 20px 10px 20px;
  border: solid #00073F 2px;
  text-decoration: none;
  width: 30vh;
  margin: 1vh
}

button:hover {
  text-decoration: none;
}

.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, .5);
  display: table;
  transition: opacity .3s ease;
}

.modal-wrapper {
  display: table-cell;
  vertical-align: middle;
}

.modal-container {
  width: 70%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  margin: 0px auto;
  padding: 20px 30px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .33);
  transition: all .3s ease;
  font-family: Helvetica, Arial, sans-serif;
}

@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    padding: 15px 20px;
  }
}

.modal-header h3 {
  margin-top: 0;
  color: #42b983;
}

.modal-body {
  margin: 20px 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
}

.modal-default-button {
  /* Was `float: right`, which escaped the container's padding and clipped at the
     edge. Also sized to the 44px minimum tap target. */
  min-height: 44px;
  padding: 0 20px;
  border: 1px solid #00073F;
  border-radius: 6px;
  background: #00073F;
  color: #fff;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.modal-default-button:hover {
  background: #001a6b;
}

.modal-default-button:focus-visible {
  outline: 2px solid #3498db;
  outline-offset: 2px;
}

/*
 * The following styles are auto-applied to elements with
 * transition="modal" when their visibility is toggled
 * by Vue.js.
 *
 * You can easily play with the modal transition by editing
 * these styles.
 */

.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}

li {
  display: block;
}
</style>
